import { useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Eye, EyeOff, Loader2, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credencialLabel: string; // ex.: "SUZANO · usuario@x"
  loading?: boolean;
  onConfirm: (userPassword: string) => void | Promise<void>;
}

export function CofreDeleteDialog({ open, onOpenChange, credencialLabel, loading, onConfirm }: Props) {
  const [pwd, setPwd] = useState('');
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!open) {
      setPwd('');
      setShow(false);
    }
  }, [open]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-[rgba(20,16,8,0.40)] backdrop-blur-[2px]',
            'data-[state=open]:animate-fade-in',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,460px)]',
            'rounded-3 border border-card-stroke bg-card shadow-clay p-6',
            'data-[state=open]:animate-fade-in',
          )}
        >
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-warn-soft text-warn">
              <AlertTriangle className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <DialogPrimitive.Title className="font-display text-[18px] font-bold tracking-[-0.015em] text-fg leading-tight">
                Excluir esta credencial?
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-fg-muted mt-1.5">
                <span className="font-mono">{credencialLabel}</span> · operação irreversível. Confirme com sua senha do GestAKI.
              </DialogPrimitive.Description>
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (pwd.length >= 6) void onConfirm(pwd);
            }}
            className="mt-5"
          >
            <Label htmlFor="cofre-del-pwd" className="mb-1.5 block">Sua senha do GestAKI</Label>
            <div className="relative">
              <Input
                id="cofre-del-pwd"
                type={show ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="••••••"
                autoComplete="current-password"
                autoFocus
                className="pr-9"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-1 text-fg-muted hover:text-fg"
                aria-label={show ? 'Ocultar' : 'Mostrar'}
              >
                {show ? <EyeOff className="h-4 w-4" strokeWidth={1.8} /> : <Eye className="h-4 w-4" strokeWidth={1.8} />}
              </button>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <Button variant="ghost" type="button" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button variant="danger" type="submit" disabled={loading || pwd.length < 6}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                    Excluindo…
                  </>
                ) : (
                  'Excluir'
                )}
              </Button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
